/**
 * Bunny CDN "Dynamic Images" (Optimizer) URL builder.
 *
 * All product/merchant images are served through a Bunny pull zone with the
 * Optimizer enabled. We keep the pull zone's **Optimizer Class** (`?class=`) as
 * the backbone because the zone may have "Force Transformation Classes" turned
 * on — in that mode requests without a class are rejected. On top of the class
 * we layer the free-form params the Optimizer also accepts (`width`, `height`,
 * `quality`, `auto_optimize`, `v` cache-bust). When a class locks a property,
 * Bunny simply ignores the redundant param, so adding them is never breaking.
 *
 * Modern-format negotiation (WebP/AVIF) is handled by Bunny automatically from
 * the browser's `Accept` header when "Automatic WebP" is enabled on the zone —
 * we deliberately do NOT hardcode `format=webp` so browsers that still need a
 * fallback keep getting one.
 *
 * Docs: https://docs.bunny.net/docs/stream-image-processing
 *       https://support.bunny.net/hc/en-us/articles/360027448392
 */

export type BunnyAutoOptimize = "low" | "medium" | "high";

export type BunnyImageFormat = "webp" | "avif" | "jpg" | "png";

export type BunnyImageOptions = {
  /** Target width in px (Optimizer resizes keeping aspect ratio). */
  width?: number;
  /** Target height in px. Combine with `width` to pin an exact box. */
  height?: number;
  /** Compression level 0–100 (Bunny default is 85; <70 gets visibly lossy). */
  quality?: number;
  /** Multi-pass smart optimization; `high` also sharpens. Omit to leave off. */
  autoOptimize?: BunnyAutoOptimize;
  /** Force an output format. Prefer leaving unset so Bunny negotiates WebP/AVIF. */
  format?: BunnyImageFormat;
  /** Gaussian blur 0–100 — handy for a server-side LQIP. */
  blur?: number;
  /** Optimizer Class configured in the Bunny dashboard (e.g. `full`, `placeholder`). */
  imageClass?: string;
  /** Cache-busting token (image CRC/version) mapped to Bunny's `v` param. */
  version?: string;
};

const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, Math.round(value)));

/**
 * Appends Bunny Optimizer params to an absolute image URL. Non-absolute or
 * malformed inputs are returned untouched (mirrors the previous helper so local
 * asset paths and odd URLs never throw).
 */
export function bunnyImageUrl(base: string, options: BunnyImageOptions = {}): string {
  try {
    const url = new URL(base);
    const params = url.searchParams;

    if (options.imageClass) params.set("class", options.imageClass);
    if (options.width) params.set("width", String(Math.round(options.width)));
    if (options.height) params.set("height", String(Math.round(options.height)));
    if (options.quality != null) params.set("quality", String(clamp(options.quality, 0, 100)));
    if (options.autoOptimize) params.set("auto_optimize", options.autoOptimize);
    if (options.format) params.set("format", options.format);
    if (options.blur != null) params.set("blur", String(clamp(options.blur, 0, 100)));
    if (options.version) params.set("v", options.version);

    return url.toString();
  } catch {
    return base;
  }
}

type ResponsiveWidthsConfig = {
  /** Upper bound so we never request an image larger than the source is worth. */
  max?: number;
  /** Device-pixel-ratio buckets to cover (1x / 2x / 3x by default). */
  densities?: number[];
};

/**
 * Given a CSS layout width, returns the sorted, de-duplicated set of candidate
 * widths for a `srcset` — one per pixel density, capped at `max`. The browser
 * then picks the smallest candidate that satisfies the current DPR + `sizes`,
 * which is how Retina/2x/3x screens get a sharp image without desktop paying
 * for it.
 */
export function responsiveWidths(
  layoutWidth: number,
  { max = 2048, densities = [1, 2, 3] }: ResponsiveWidthsConfig = {},
): number[] {
  const widths = new Set<number>();
  for (const density of densities) {
    widths.add(Math.min(max, Math.round(layoutWidth * density)));
  }
  return [...widths].sort((a, b) => a - b);
}

/**
 * Builds a Bunny `srcset` string for the given candidate widths. Each entry
 * keeps the same shape by scaling `height` proportionally when a height is
 * supplied, so every DPR variant crops identically.
 */
export function bunnySrcSet(
  base: string,
  widths: number[],
  options: BunnyImageOptions & { layoutWidth?: number } = {},
): string {
  const { layoutWidth, height, ...rest } = options;
  return widths
    .map((width) => {
      const scaledHeight =
        height && layoutWidth ? Math.round((width * height) / layoutWidth) : height;
      return `${bunnyImageUrl(base, { ...rest, width, height: scaledHeight })} ${width}w`;
    })
    .join(", ");
}
